import { useT } from 'apprise-frontend-core/intl/language'
import { useAsyncTask } from 'apprise-ui/utils/asynctask'
import { useXlsxParserConfig } from './context'
import { useJsonParser, useXlsxParser } from './file'
import { ItemParser, ModelParser, ParseContext, ParseIssue, ParsePostProcessor, Resource, ResourceParser } from './model'
import { WorkbookParser } from './workbook'


type DefaultParserProps<T, S> = {

  modelparser: ModelParser<T, S>
  bookparser: WorkbookParser<T, S>
  postProcess?: ParsePostProcessor<T, S>

}

export const useDefaultParser = <T, S>(props: DefaultParserProps<T, S>): ResourceParser<T, S> => {

  const t = useT()
  const task = useAsyncTask()


  const { modelparser: parseModel, bookparser: parseBook, postProcess = t => t } = props

  const cfg = useXlsxParserConfig()

  const parseJson = useJsonParser<T, S>()
  const parseXlsx = useXlsxParser<T, S>()

  return task.make(async (resource: Resource, ctx: ParseContext<S>) => {

    const type = resource.file.type

    if (cfg?.xslxMimeTypes?.includes(type))
      return parseXlsx(resource).with(parseBook, ctx).then(o => postProcess(o, ctx))

    if (cfg?.jsonMimeTypes?.includes(type))
      return parseJson(resource).with(parseModel, ctx).then(o => postProcess(o, ctx))

    throw Error(`unknown file type: ${type} `)


  }).with(config => config

    .show(resource => t('parse.msg', { name: resource.name }))

  ).done()

}


export const useDefaultModelParser = <T, S>(parser: ItemParser<T, S>): ModelParser<T, S> => {

  const t = useT()

  return (rows: any[], ctx: ParseContext<S>) => {

    const issues: ParseIssue[] = []
    const data = rows.map(row => {

      try {

        return parser(row, ctx)

      }
      catch (e: any) {

        const type = e.type ?? 'error'

        issues.push({ type, message: e?.message ?? t('parse.unspecified_error') });
        
        return undefined!
      }

    }).filter(p => !!p)

    return { data, issues }

  }
}